import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Check, Lightbulb } from 'lucide-react';
import { Target, Sparkles, Coins, PenTool, Zap } from 'lucide-react';
export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    services: [] as string[],
    projectDetails: '',
    budget: '',
    timeline: ''
  });
  const serviceOptions = [{
    id: 'strategy',
    label: 'Strategy & GTM',
    icon: Target
  }, {
    id: 'design',
    label: 'Design & Websites',
    icon: Sparkles
  }, {
    id: 'investor',
    label: 'Investor Materials',
    icon: Coins
  }, {
    id: 'content',
    label: 'Content & Copy',
    icon: PenTool
  }, {
    id: 'automation',
    label: 'Automation & Tools',
    icon: Zap
  }, {
    id: 'other',
    label: 'Something Else',
    icon: Lightbulb
  }];
  const handleServiceToggle = (serviceId: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId) ? prev.services.filter(s => s !== serviceId) : [...prev.services, serviceId]
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Redirect to Calendly with pre-filled information
    const calendlyUrl = 'https://calendly.com/thesprintstudio';
    const params = new URLSearchParams({
      name: formData.name,
      email: formData.email
    });
    window.open(`${calendlyUrl}?${params.toString()}`, '_blank');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };
  return <section id="contact" className="py-32 bg-paper" data-id="element-1">
      <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-2">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-3">
          <span className="text-terracotta font-medium tracking-[0.2em] uppercase text-xs mb-4 block" data-id="element-4">
            Ready to Get Started?
          </span>
          <h2 className="text-5xl md:text-6xl font-serif text-ink mb-6 font-normal" data-id="element-5">
            Start a Project
          </h2>
          <p className="text-lg text-ink/70 max-w-xl mx-auto font-normal" data-id="element-6">
            Tell us what you need and we'll get back to you within 24 hours with
            a plan.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} data-id="element-7">
          <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 space-y-8" data-id="element-8">
            {/* Service Selection */}
            <div data-id="element-9">
              <h3 className="text-xl font-serif text-ink mb-2 font-normal" data-id="element-10">
                What do you need?
              </h3>
              <p className="text-sm text-ink/60 mb-6 font-normal" data-id="element-11">
                Select all that apply. We'll figure out the details together.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-12">
                {serviceOptions.map(service => {
                const Icon = service.icon;
                const isSelected = formData.services.includes(service.id);
                return <button key={service.id} type="button" onClick={() => handleServiceToggle(service.id)} className={`flex items-center gap-3 p-4 border transition-all text-left ${isSelected ? 'border-terracotta bg-terracotta/5' : 'border-ink/20 hover:border-ink/40'}`} data-id="element-13">
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-terracotta' : 'text-ink/60'}`} strokeWidth={1.5} data-id="element-14" />
                      <span className="flex-1 font-normal text-ink text-sm" data-id="element-15">
                        {service.label}
                      </span>
                      {isSelected && <Check className="w-5 h-5 text-terracotta" strokeWidth={2} data-id="element-16" />}
                    </button>;
              })}
              </div>
            </div>

            {/* Project Details */}
            <div data-id="element-17">
              <label htmlFor="projectDetails" className="block text-sm font-medium text-ink mb-2" data-id="element-18">
                Tell us more about your project *
              </label>
              <textarea id="projectDetails" name="projectDetails" required value={formData.projectDetails} onChange={handleChange} rows={5} className="w-full px-4 py-3 border border-ink/20 focus:border-terracotta focus:outline-none focus:ring-1 focus:ring-terracotta transition-colors resize-none" placeholder="The more detail you share, the better we can help..." data-id="element-19" />
              <p className="text-xs text-ink/50 mt-2" data-id="element-20">
                The more detail you share, the better we can help.
              </p>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-21">
              <div data-id="element-22">
                <label htmlFor="name" className="block text-sm font-medium text-ink mb-2" data-id="element-23">
                  Name *
                </label>
                <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-ink/20 focus:border-terracotta focus:outline-none focus:ring-1 focus:ring-terracotta transition-colors" placeholder="Your name" data-id="element-24" />
              </div>

              <div data-id="element-25">
                <label htmlFor="email" className="block text-sm font-medium text-ink mb-2" data-id="element-26">
                  Email *
                </label>
                <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-ink/20 focus:border-terracotta focus:outline-none focus:ring-1 focus:ring-terracotta transition-colors" placeholder="you@company.com" data-id="element-27" />
              </div>
            </div>

            <div data-id="element-28">
              <label htmlFor="company" className="block text-sm font-medium text-ink mb-2" data-id="element-29">
                Company
              </label>
              <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 border border-ink/20 focus:border-terracotta focus:outline-none focus:ring-1 focus:ring-terracotta transition-colors" placeholder="Your company name" data-id="element-30" />
            </div>

            {/* Budget & Timeline */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-31">
              <div data-id="element-32">
                <label htmlFor="budget" className="block text-sm font-medium text-ink mb-2" data-id="element-33">
                  Budget Range
                </label>
                <select id="budget" name="budget" value={formData.budget} onChange={handleChange} className="w-full px-4 py-3 border border-ink/20 focus:border-terracotta focus:outline-none focus:ring-1 focus:ring-terracotta transition-colors bg-white" data-id="element-34">
                  <option value="" data-id="element-35">Select a range</option>
                  <option value="Under $500" data-id="element-36">Under $500</option>
                  <option value="$500 - $1,000" data-id="element-37">$500 - $1,000</option>
                  <option value="$1,000 - $2,500" data-id="element-38">$1,000 - $2,500</option>
                  <option value="$2,500 - $5,000" data-id="element-39">$2,500 - $5,000</option>
                  <option value="$5,000+" data-id="element-40">$5,000+</option>
                  <option value="Monthly Retainer" data-id="element-41">Monthly Retainer</option>
                </select>
              </div>

              <div data-id="element-42">
                <label htmlFor="timeline" className="block text-sm font-medium text-ink mb-2" data-id="element-43">
                  Timeline
                </label>
                <select id="timeline" name="timeline" value={formData.timeline} onChange={handleChange} className="w-full px-4 py-3 border border-ink/20 focus:border-terracotta focus:outline-none focus:ring-1 focus:ring-terracotta transition-colors bg-white" data-id="element-44">
                  <option value="" data-id="element-45">Select timeline</option>
                  <option value="ASAP" data-id="element-46">ASAP</option>
                  <option value="Within 1 week" data-id="element-47">Within 1 week</option>
                  <option value="Within 2 weeks" data-id="element-48">Within 2 weeks</option>
                  <option value="Within 1 month" data-id="element-49">Within 1 month</option>
                  <option value="Flexible" data-id="element-50">Flexible</option>
                </select>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4" data-id="element-51">
              <button type="submit" className="w-full bg-ink text-white px-8 py-4 text-base font-medium hover:bg-ink/90 transition-colors flex items-center justify-center gap-2" data-id="element-52">
                Book a Call
                <Calendar className="w-4 h-4" data-id="element-53" />
              </button>
              <p className="text-xs text-ink/50 text-center mt-4" data-id="element-54">
                No commitment required. We'll review your info and chat on the
                call.
              </p>
            </div>
          </form>
        </motion.div>
      </div>
    </section>;
}